import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePageTitle } from "@/hooks/usePageTitle";
import { PageHeader } from "@/components/PageHeader";
import { SkillBadge } from "@/components/SkillBadge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, Briefcase, Check, Download, User, X, Brain, MessageSquare, FileText, ClipboardCheck, AlertTriangle, Target, Loader2, Mail, PieChart, History, HelpCircle } from "lucide-react";
import { BackButton } from "@/components/ui/BackButton";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DynamicTabBar, Tab } from "@/components/ui/DynamicTabBar";
import ReactMarkdown from 'react-markdown';
import React from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { MatchComments } from "@/components/matches/MatchComments";
import { InterviewPrep } from "@/components/matches/InterviewPrep";
import MatchDocumentsTab from "@/components/matches/MatchDocumentsTab";
import { AIAnalysisProgress } from "@/components/ui/AIAnalysisProgress";
import { useAIAnalysisProgress } from "@/hooks/useAIAnalysisProgress";
import { MatchProfilesTab } from "@/components/matches/MatchProfilesTab";
import { NotificationSubscriptionButton } from "@/components/notifications/NotificationSubscriptionButton";
import { SuperAdminReanalyzeButton } from "@/components/admin/SuperAdminReanalyzeButton";
import { SuperAdminDeleteMatchButton } from "@/components/admin/SuperAdminDeleteMatchButton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

import { SkillsComparisonGraph } from "@/components/matches/SkillsComparisonGraph";
import { MatchCustomFieldsTab } from "@/components/matches/MatchCustomFieldsTab";
import { MatchCircularOverview } from "@/components/matches/MatchCircularOverview";
import { MatchTasksTab } from "@/components/matches/MatchTasksTab";
import { fetchCustomFields, CustomFieldsJSON } from "@/integrations/supabase/customFieldsApi";
import { useAuth } from "@/providers/AuthProvider";
import { triggerHiredConfetti } from "@/utils/confetti";
import { EntityMailbox } from "@/components/inbox/EntityMailbox";
import { useTrackPageVisit } from "@/hooks/useEntityHistory";
import { MatchHistoryTab } from "@/components/matches/MatchHistoryTab";
import { getKanbanStageId } from "@/components/jobs/kanban/types/index";
import { useKanbanColumns } from "@/hooks/useKanbanColumns";
import { CLOSED_MATCH_STATUSES } from "@/utils/matches/status";
import { getAllowedNextTimelineStatuses, isActiveTimelineStage, isClosedTimelineStatus } from "@/utils/matches/timelineStatus";
import { createWorkforce } from "@/hooks/workforce/useWorkforceActions";
import {
    CURRENCY_OPTIONS,
    LOCATION_TYPES,
    CONTRACT_TYPES,
} from "@/types/workforce";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { explanationText } from "@/utils/matches/analysisText";

const MatchDetail = () => {
    const { t } = useTranslation();
    const { stages: KANBAN_STATUSES, getStatusLabel } = useKanbanColumns();

    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [match, setMatch] = useState<any>(null);

    usePageTitle(match?.candidates?.name && match?.jobs?.title ? `${match.candidates.name} - ${match.jobs.title}` : "Match");

    // Track page visit for history
    const matchMetadata = useMemo(() => ({
        companyName: match?.jobs?.companies?.name
    }), [match?.jobs?.companies?.name]);

    // Compute entity name for tracking
    const matchEntityName = useMemo(() => {
        if (!match?.id) return undefined;
        if (match?.candidates?.name && match?.jobs?.title) {
            return `${match.candidates.name} - ${match.jobs.title}`;
        }
        return `Match ${match.id.slice(0, 8)}`;
    }, [match?.id, match?.candidates?.name, match?.jobs?.title]);

    useTrackPageVisit('match', match?.id, matchEntityName, matchMetadata);

    const [activeTab, setActiveTab] = useState("analysis");
    const [openAccordionItems, setOpenAccordionItems] = useState<string[]>(["analysis", "strengths", "weaknesses", "skills"]);
    const [missingSkills, setMissingSkills] = useState<string[]>([]);
    const [missingSkillsLoading, setMissingSkillsLoading] = useState(false);
    // Use the new hook for AI analysis progress
    useAIAnalysisProgress(id, "match");
    const [reportOpen, setReportOpen] = useState(false);
    const [customFields, setCustomFields] = useState([]);
    const [customFieldsLoading, setCustomFieldsLoading] = useState(false);
    const [customFieldsError, setCustomFieldsError] = useState<string | null>(null);
    const { currentOrganization, user } = useAuth();
    const organizationId = currentOrganization?.id;
    const [previousStatusBeforeCurrent, setPreviousStatusBeforeCurrent] = useState<string | null>(null);
    
    const matchTabConfig: Tab[] = useMemo(() => {
        const tabs: Tab[] = [
            { id: "analysis", label: "Match Analysis", icon: <PieChart className="h-4 w-4" /> },
            { id: "profiles", label: "Profiles", icon: <Brain className="h-4 w-4" /> },
            { id: "comments", label: t('matching.tabs.comments'), icon: <MessageSquare className="h-4 w-4" /> },
            { id: "interview-prep", label: t('matching.tabs.interviewPrep'), icon: <Brain className="h-4 w-4" /> },
            { id: "documents", label: t('matching.tabs.documents', 'Documents'), icon: <FileText className="h-4 w-4" /> },
            { id: "tasks", label: t('matching.tabs.tasks'), icon: <ClipboardCheck className="h-4 w-4" /> },
        ];
        if (customFields.length > 0)
            tabs.push({ id: "custom-fields", label: t('candidates.tabs.matchesCustomFields'), icon: <FileText className="h-4 w-4" /> });
        tabs.push(
            { id: "mailbox", label: "Mailbox", icon: <Mail className="h-4 w-4" /> },
            { id: "history", label: t('matching.tabs.history'), icon: <History className="h-4 w-4" /> },
        );
        return tabs;
    }, [t, customFields.length]);

    // State for tracking outdated analysis
    const [isOutdated, setIsOutdated] = useState(false);
    const [outdatedReasons, setOutdatedReasons] = useState<string[]>([]);
    const [isReAnalyzing, setIsReAnalyzing] = useState(false);

    // State for status change confirmation dialog
    const [statusConfirmDialog, setStatusConfirmDialog] = useState<{
        open: boolean;
        targetStatus: string;
        actions: { key: string; label: string; description: string; checked: boolean; value?: string; }[];
    }>({ open: false, targetStatus: '', actions: [] });

    // State for inline workforce creation form (within status confirm dialog)
    const [workforceForm, setWorkforceForm] = useState({
        startDate: new Date().toISOString().split("T")[0],
        expectedEndDate: "",
        location: "",
        locationType: "" as string,
        contractType: "" as string,
        currency: "EUR",
        purchasePriceDaily: "",
        sellingPriceDaily: "",
        notes: "",
    });

    const fetchMatch = async () => {
        if (!id) return;

        try {
            setLoading(true);
            const { data, error } = await supabase
                .from('matches')
                .select(`
					*,
					candidates (
						id,
						name,
						email,
						function,
						skills,
						ai_synthesis,
						ai_last_updated
					),
					jobs (
						id,
						title,
						description,
						skills,
						hard_skills,
						soft_skills,
						ai_last_updated,
						companies (
							id,
							name
						)
					)
				`)
                .eq('id', id)
                .single();

            if (error) throw error;

            setMatch({
                ...data,
                custom_fields: (data as any).custom_fields || {},
            });
            
            // Check if match is outdated
            checkIfOutdated(data);
        } catch (error) {
            console.error("Error fetching match:", error);
            toast({
                title: t('common.error'),
                description: t('matching.errors.failedToLoadDetails'),
                variant: "destructive",
            });
        } finally {
            setLoading(false);
        }
    };

    // Check if the match analysis is outdated based on ai_last_updated fields
    const checkIfOutdated = (matchData: any) => {
        if (!matchData || !matchData.updated_at) return;
        
        const matchUpdatedAt = new Date(matchData.updated_at);
        const reasons: string[] = [];
        
        // Check candidate ai_last_updated
        if (matchData.candidates?.ai_last_updated) {
            const candidateAiUpdated = new Date(matchData.candidates.ai_last_updated);
            if (candidateAiUpdated > matchUpdatedAt) {
                reasons.push(t('matching.outdated.candidateUpdated'));
            }
        }
        
        // Check job ai_last_updated
        if (matchData.jobs?.ai_last_updated) {
            const jobAiUpdated = new Date(matchData.jobs.ai_last_updated);
            if (jobAiUpdated > matchUpdatedAt) {
                reasons.push(t('matching.outdated.jobUpdated'));
            }
        }
        
        setIsOutdated(reasons.length > 0);
        setOutdatedReasons(reasons);
    };

    // Re-trigger the match analysis
    const handleReAnalyze = async () => {
        if (!id || !match) return;
        
        setIsReAnalyzing(true);
        
        try {
            const { data, error } = await supabase.functions.invoke('ai-processing?command=push-new-analysis', {
                method: 'POST',
                body: {
                    entityId: id,
                    entityType: "match",
                    analysisType: "full-match",
                    actionOnComplete: "update-match",
                }
            });

            if (error) throw error;

            // Hide the outdated banner immediately
            setIsOutdated(false);
            setOutdatedReasons([]);

            toast({
                title: t('common.success'),
                description: t('matching.success.reAnalyzed'),
                variant: "default",
            });

            // The AI analysis progress component will automatically update
            // No need to refresh immediately

        } catch (error) {
            console.error("Error re-analyzing match:", error);
            toast({
                title: t('common.error'),
                description: t('matching.errors.failedToReAnalyze'),
                variant: "destructive",
            });
        } finally {
            setIsReAnalyzing(false);
        }
    };

    useEffect(() => {
        fetchMatch();
        // Reset missing skills when match ID changes
        setMissingSkills([]);
        setMissingSkillsLoading(false);
        // Reset outdated status when match ID changes
        setIsOutdated(false);
        setOutdatedReasons([]);
    }, [id]);

    useEffect(() => {
        if (!organizationId) return;
        setCustomFieldsLoading(true);
        setCustomFieldsError(null);
        fetchCustomFields(organizationId)
            .then((data: CustomFieldsJSON) => {
                setCustomFields(data.Matches || []);
            })
            .catch(() => {
                setCustomFieldsError("Failed to load custom fields");
            })
            .finally(() => setCustomFieldsLoading(false));
    }, [organizationId]);

    useEffect(() => {
        const fetchPreviousStatus = async () => {
            if (!id || !match?.status || !isClosedTimelineStatus(match.status)) {
                setPreviousStatusBeforeCurrent(null);
                return;
            }

            try {
                const { data, error } = await supabase
                    .from('history_matches' as any)
                    .select('changed_at, old_value, new_value')
                    .eq('match_id', id)
                    .order('changed_at', { ascending: false })
                    .limit(20);

                if (error)
                    throw error;

                const previousStatus = (data || []).find((entry: any) => entry?.new_value?.status === match.status && entry?.old_value?.status)?.old_value?.status || null;
                setPreviousStatusBeforeCurrent(previousStatus);
            } catch (error) {
                console.error("Error fetching match status history:", error);
                setPreviousStatusBeforeCurrent(null);
            }
        };

        fetchPreviousStatus();
    }, [id, match?.status]);

    const buildStatusActions = (stageId: string) => {
        const actions: { key: string; label: string; description: string; checked: boolean; }[] = [];
        const candidateName = match?.candidates?.name || t('common.candidate');
        const jobTitle = match?.jobs?.title || t('common.job');

        if (stageId === 'hired') {
            actions.push({
                key: 'create_workforce',
                label: t('matching.statusConfirm.actions.createWorkforce', 'Create a workforce assignment'),
                description: t('matching.statusConfirm.actions.createWorkforceDesc', 'Create a workforce entry for {{candidateName}} on {{jobTitle}}', { candidateName, jobTitle }),
                checked: false,
            });
            actions.push({
                key: 'set_candidate_hired',
                label: t('matching.statusConfirm.actions.setCandidateHired'),
                description: t('matching.statusConfirm.actions.setCandidateHiredDesc', { candidateName }),
                checked: true,
            });
            actions.push({
                key: 'close_candidate_matches',
                label: t('matching.statusConfirm.actions.closeCandidateMatches'),
                description: t('matching.statusConfirm.actions.closeCandidateMatchesDesc', { candidateName }),
                checked: true,
            });
            actions.push({
                key: 'close_job',
                label: t('matching.statusConfirm.actions.closeJobWon'),
                description: t('matching.statusConfirm.actions.closeJobWonDesc', { jobTitle }),
                checked: true,
            });
            actions.push({
                key: 'close_job_matches',
                label: t('matching.statusConfirm.actions.closeJobMatches'),
                description: t('matching.statusConfirm.actions.closeJobMatchesDesc', { jobTitle }),
                checked: true,
            });

            // Reset workforce form
            setWorkforceForm({
                startDate: new Date().toISOString().split("T")[0],
                expectedEndDate: "",
                location: "",
                locationType: "",
                contractType: "",
                currency: "EUR",
                purchasePriceDaily: "",
                sellingPriceDaily: "",
                notes: "",
            });
        }

        if (stageId === 'candidate_not_available') {
            actions.push({
                key: 'set_candidate_unavailable',
                label: t('matching.statusConfirm.actions.setCandidateUnavailable'),
                description: t('matching.statusConfirm.actions.setCandidateUnavailableDesc', { candidateName }),
                checked: true,
            });
            actions.push({
                key: 'close_candidate_matches',
                label: t('matching.statusConfirm.actions.closeCandidateMatches'),
                description: t('matching.statusConfirm.actions.closeCandidateMatchesDesc', { candidateName }),
                checked: true,
            });
        }

        if (stageId === 'application_closed') {
            actions.push({
                key: 'close_job',
                label: t('matching.statusConfirm.actions.closeJob'),
                description: t('matching.statusConfirm.actions.closeJobDesc', { jobTitle }),
                checked: true,
                value: 'closed/lost',
            });
            actions.push({
                key: 'close_job_matches',
                label: t('matching.statusConfirm.actions.closeJobMatches'),
                description: t('matching.statusConfirm.actions.closeJobMatchesDesc', { jobTitle }),
                checked: true,
            });
        }

        return actions;
    };

    const updateMatchStage = async (stageId: string) => {
        if (!id || stageId === match?.status) return;

        // For statuses with side effects, show confirmation dialog
        if (stageId === 'hired' || stageId === 'candidate_not_available' || stageId === 'application_closed') {
            setStatusConfirmDialog({
                open: true,
                targetStatus: stageId,
                actions: buildStatusActions(stageId),
            });
            return;
        }

        await performStatusUpdate(stageId);
    };

    const performStatusUpdate = async (stageId: string) => {
        if (!id) return;

        try {
            const { error } = await supabase
                .from('matches')
                .update({ status: stageId })
                .eq('id', id);

            if (error) throw error;

            // Update local state
            setMatch({
                ...match,
                status: stageId
            });

            toast({
                title: t('common.success'),
                description: t('matching.success.matchMoved', { stage: getStatusLabel(stageId) }),
                variant: "default",
            });

            // Trigger confetti when a candidate is hired! 🎉
            if (stageId === 'hired') {
                triggerHiredConfetti();
            }

            return true;
        } catch (error) {
            toast({
                title: t('common.error'),
                description: t('matching.errors.failedToUpdateStage'),
                variant: "destructive",
            });
            return false;
        }
    };

    const toggleStatusAction = (key: string) => {
        setStatusConfirmDialog(prev => ({
            ...prev,
            actions: prev.actions.map(a => a.key === key ? { ...a, checked: !a.checked } : a),
        }));
    };

    const updateStatusActionValue = (key: string, value: string) => {
        setStatusConfirmDialog(prev => ({
            ...prev,
            actions: prev.actions.map(a => a.key === key ? { ...a, value } : a),
        }));
    };

    const handleStatusConfirmApply = async () => {
        const { targetStatus, actions } = statusConfirmDialog;
        setStatusConfirmDialog(prev => ({ ...prev, open: false }));

        const candidateId = match?.candidate_id;
        const jobId = match?.job_id;

        const success = await performStatusUpdate(targetStatus);
        if (!success) return;

        const checkedKeys = new Set(actions.filter(a => a.checked).map(a => a.key));

        try {
            // Create workforce entry if requested
            if (checkedKeys.has('create_workforce') && candidateId && jobId) {
                // Get organization UUID
                const { data: { user } } = await supabase.auth.getUser();
                if (user) {
                    const { data: userOrg } = await supabase
                        .from('user_organization')
                        .select('organization_uuid')
                        .eq('user_uuid', user.id)
                        .single();

                    if (userOrg?.organization_uuid) {
                        await createWorkforce(
                            {
                                candidate_id: candidateId,
                                job_id: jobId,
                                start_date: workforceForm.startDate,
                                expected_end_date: workforceForm.expectedEndDate || undefined,
                                status: "active",
                                location: workforceForm.location || undefined,
                                location_type: (workforceForm.locationType || undefined) as any,
                                contract_type: (workforceForm.contractType || undefined) as any,
                                currency: workforceForm.currency,
                                purchase_price_daily: workforceForm.purchasePriceDaily || undefined,
                                selling_price_daily: workforceForm.sellingPriceDaily || undefined,
                                notes: workforceForm.notes || undefined,
                            },
                            userOrg.organization_uuid
                        );
                    }
                }
            }

            // Candidate-related actions
            if (checkedKeys.has('set_candidate_hired') && candidateId)
                await supabase.from('candidates').update({ status: 'hired' }).eq('id', candidateId);

            if (checkedKeys.has('set_candidate_unavailable') && candidateId)
                await supabase.from('candidates').update({ status: 'unavailable' }).eq('id', candidateId);

            if (checkedKeys.has('close_candidate_matches') && candidateId)
                await supabase.rpc('set_match_candidate_not_available', { p_candidate_id: candidateId });

            // Job-related actions
            if (checkedKeys.has('close_job') && jobId) {
                const closeJobAction = actions.find(a => a.key === 'close_job');
                const jobStatus = closeJobAction?.value || (targetStatus === 'hired' ? 'closed/won' : 'closed/lost');
                await supabase.from('jobs').update({ status: jobStatus, published: false, publishdate: null }).eq('id', jobId);
                await supabase.from('jobs_public').delete().eq('job_id', jobId);
            }

            if (checkedKeys.has('close_job_matches') && jobId)
                await supabase.rpc('set_match_application_closed', { p_job_id: jobId });
        } catch (error) {
            toast({
                title: t('common.error'),
                description: t('matching.statusConfirm.sideEffectError'),
                variant: "destructive",
            });
        }
    };

    const closeStatusConfirmDialog = () => {
        setStatusConfirmDialog(prev => ({ ...prev, open: false }));
    };

    // Add a helper for formatting criteria names
    function formatCriteriaName(name: string) {
        return name
            .replace(/_/g, ' ')
            .replace(/\b\w/g, c => c.toUpperCase());
    }

    // Add a simple neutral gauge bar component
    const NeutralGauge = ({ value }: { value: number }) => (
        <div className="flex items-center gap-2 w-full">
            <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-sm overflow-hidden">
                <div
                    className="h-2 rounded-sm transition-all"
                    style={{ width: `${Math.max(0, Math.min(1, value)) * 100}%`, backgroundColor: 'rgb(117, 160, 233)' }}
                />
            </div>
            <span className="ml-2 text-xs tabular-nums text-gray-700 dark:text-gray-200" style={{ minWidth: 32 }}>{value}</span>
        </div>
    );

    // Helper component for pretty analysis rendering
    const DetailedAnalysis = ({ analysis }: { analysis: any }) => {
        if (!analysis || typeof analysis !== "object") return null;
        return (
            <div className="space-y-6">
                {Object.entries(analysis).map(([section, value]: [string, any]) => {
                    const pairScores = value["Pair score"] && typeof value["Pair score"] === 'object'
                        ? Object.entries(value["Pair score"]) as [string, any][]
                        : [];
                    return (
                        <div key={section} className="bg-gray-50 dark:bg-gray-800/50 rounded-sm p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                            <h5 className="text-lg font-semibold mb-2 text-digger-blue">{section}</h5>
                            {value.Explanation && (
                                <div className="mb-2 text-gray-700 dark:text-gray-200">
                                    <span className="font-medium">Explanation:</span> {value.Explanation}
                                </div>
                            )}							{pairScores.length > 0 && (
                                <div className="mb-2">
                                    <span className="font-medium text-gray-600 dark:text-gray-300">{t('matching.analysis.pairScores')}:</span>
                                    <div className="bg-white dark:bg-gray-800 rounded-sm border border-gray-100 dark:border-gray-700 mt-2">
                                        <div className="grid grid-cols-1 gap-y-2 p-2">
                                            {pairScores.map(([k, v]) => (
                                                <div key={k} className="flex items-center gap-4">
                                                    <div className="font-mono text-xs text-gray-500 dark:text-gray-400 truncate flex-1" style={{ fontFamily: 'inherit', fontWeight: 500 }}>{formatCriteriaName(k)}</div>
                                                    <div className="w-2/3 max-w-xs min-w-[80px]"><NeutralGauge value={v} /></div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {value["Match Ratio"] !== undefined && (
                                <div className="mb-1 text-sm text-gray-600 dark:text-gray-300">
                                    <span className="font-medium">{t('matching.analysis.matchRatio')}:</span> {value["Match Ratio"]}
                                </div>
                            )}
                            {value["Weighted Score"] !== undefined && (
                                <div className="mb-1 text-sm text-gray-600 dark:text-gray-300">
                                    <span className="font-medium">{t('matching.analysis.weightedScore')}:</span> {value["Weighted Score"]}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };

    // PDF Download Handler
    const handleDownloadReport = async () => {
        const reportElement = document.getElementById("match-report-modal-content");
        if (!reportElement) return;

        const canvas = await html2canvas(reportElement, { scale: 2, backgroundColor: '#fff' });
        const imgWidth = 515; // A4 width minus margins (in pt)
        const pageHeight = 742; // A4 height minus margins (in pt)
        const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

        let renderedHeight = 0;
        let pageNum = 0;

        while (renderedHeight < canvas.height) {
            // Create a canvas for the current page
            const pageCanvas = document.createElement("canvas");
            pageCanvas.width = canvas.width;
            pageCanvas.height = Math.min(canvas.height - renderedHeight, (pageHeight * canvas.width) / imgWidth);

            const ctx = pageCanvas.getContext("2d");
            if (ctx) {
                ctx.drawImage(
                    canvas,
                    0, renderedHeight, canvas.width, pageCanvas.height,
                    0, 0, canvas.width, pageCanvas.height
                );
            }

            const pageImgData = pageCanvas.toDataURL("image/png");
            if (pageNum > 0) pdf.addPage();
            pdf.addImage(pageImgData, "PNG", 40, 40, imgWidth, (pageCanvas.height * imgWidth) / canvas.width);

            renderedHeight += pageCanvas.height;
            pageNum++;
        }

        pdf.save(`Digger_Match_Report_${match.candidates?.name || "candidate"}_${match.jobs?.title || "job"}.pdf`);
    };

    // Send by Email Handler (fallback: mailto with instructions)
    const handleSendByEmail = async () => {
        const reportElement = document.getElementById("match-report-modal-content");
        if (!reportElement) return;

        const canvas = await html2canvas(reportElement, { scale: 2, backgroundColor: '#fff' });
        const imgWidth = 515;
        const pageHeight = 742;
        const pdf = new jsPDF({ orientation: "portrait", unit: "pt", format: "a4" });

        let renderedHeight = 0;
        let pageNum = 0;

        while (renderedHeight < canvas.height) {
            const pageCanvas = document.createElement("canvas");
            pageCanvas.width = canvas.width;
            pageCanvas.height = Math.min(canvas.height - renderedHeight, (pageHeight * canvas.width) / imgWidth);

            const ctx = pageCanvas.getContext("2d");
            if (ctx) {
                ctx.drawImage(
                    canvas,
                    0, renderedHeight, canvas.width, pageCanvas.height,
                    0, 0, canvas.width, pageCanvas.height
                );
            }

            const pageImgData = pageCanvas.toDataURL("image/png");
            if (pageNum > 0) pdf.addPage();
            pdf.addImage(pageImgData, "PNG", 40, 40, imgWidth, (pageCanvas.height * imgWidth) / canvas.width);

            renderedHeight += pageCanvas.height;
            pageNum++;
        }
        const pdfBlob = pdf.output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(`mailto:?subject=${t('matching.email.subject')}&body=${t('matching.email.body', { url: pdfUrl })}`);
        setTimeout(() => URL.revokeObjectURL(pdfUrl), 10000);
    };

    if (loading) {
        return (
            <div className="animate-pulse p-6">
                <div className="h-12 bg-gray-200 dark:bg-gray-700 rounded mb-6"></div>
                <div className="space-y-4">
                    <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded"></div>
                    <div className="h-64 bg-gray-200 dark:bg-gray-700 rounded"></div>
                </div>
            </div>
        );
    }

    if (!match) {
        return (
            <div className="p-6">
                <div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6 text-center">
                    <h2 className="text-xl font-semibold mb-2">{t('matching.errors.matchNotFound')}</h2>
                    <p className="text-gray-600 dark:text-gray-300">{t('matching.errors.matchNotFoundDescription')}</p>
                </div>
            </div>
        );
    }

    const currentStatus = match.status || 'matched';
    const currentKanbanStage = getKanbanStageId(currentStatus, KANBAN_STATUSES.map(status => status.id));
    const visibleActiveStage = currentKanbanStage === 'refused'
        ? (previousStatusBeforeCurrent && isActiveTimelineStage(previousStatusBeforeCurrent)
            ? previousStatusBeforeCurrent
            : null)
        : currentKanbanStage;
    const activeTimelineStatuses = KANBAN_STATUSES.filter(status => status.id !== 'refused');
    const visibleActiveStageIndex = visibleActiveStage
        ? activeTimelineStatuses.findIndex(status => status.id === visibleActiveStage)
        : -1;
    const allowedNextStatuses = getAllowedNextTimelineStatuses(currentStatus, previousStatusBeforeCurrent);
    const recommendedStatuses = allowedNextStatuses.filter(status => status !== currentStatus);
    const timelineClosedStatuses = CLOSED_MATCH_STATUSES;

    return (
        <div className="flex flex-col gap-4">
            {/* Back to matches link */}

            <AIAnalysisProgress
                entityId={id}
                entityType="match"
                onRefresh={fetchMatch}
            />

            {/* Outdated Analysis Banner */}
            {isOutdated && (
                <Alert className="border-blue-200 bg-blue-50">
                    <AlertTriangle className="h-4 w-4 text-blue-500" />
                    <AlertDescription className="flex items-center justify-between w-full">
                        <div>
                            <span className="font-medium text-blue-800 dark:text-blue-300">
                                {t('matching.outdated.title')}
                            </span>
                            <br />
                            <span className="text-blue-700 dark:text-blue-400">
                                {outdatedReasons.join(' & ')} {t('matching.outdated.description')}
                            </span>
                        </div>
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleReAnalyze}
                            disabled={isReAnalyzing}
                            className="ml-4 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-900/50"
                        >
                            {isReAnalyzing ? (
                                <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    {t('matching.outdated.analyzing')}
                                </>
                            ) : (
                                <>
                                    <Brain className="h-4 w-4 mr-2" />
                                    {t('matching.outdated.reAnalyze')}
                                </>
                            )}
                        </Button>
                    </AlertDescription>
                </Alert>
            )}

            <div className="mb-2">
                <BackButton 
                    to="/matches" 
                    label={t('matching.backToMatches')}
                    className="flex items-center text-digger-navy hover:underline text-base font-medium"
                    iconClassName="h-5 w-5 mr-1"
                />
            </div>			            <PageHeader
                title={t('matching.title')}
                actions={
                    <div className="flex gap-2 pt-2">
                        <Button variant="outline" size="icon" className="flex items-center justify-center" onClick={() => setReportOpen(true)} title={t('matching.fullReport')}>
                            <Download className="h-4 w-4" />
                        </Button>
                        <NotificationSubscriptionButton
                            entityType="match"
                            entityId={id || ''}
                            entityName={`${match?.candidates?.name || ''} → ${match?.jobs?.title || ''}`}
                            variant="outline"
                            size="icon"
                        />
                        <SuperAdminReanalyzeButton entityType="match" entityId={id || ''} />
                        <SuperAdminDeleteMatchButton matchId={id || ''} />
                    </div>
                }
                className="mb-2"
            />

            {/* Match Timeline */}
            <div className="bg-white dark:bg-gray-800 rounded-sm shadow px-6 py-5 mb-4" data-tour="match-timeline">
                <div className="flex flex-col gap-5">
                    <div className="overflow-x-auto">
                        <div className="min-w-[720px] relative px-2 pt-1">
                            <div className="absolute left-[2%] right-[2%] top-[11px] h-px bg-slate-200 dark:bg-slate-700" />
                            <div className="relative flex items-start justify-between gap-2">
                                {activeTimelineStatuses.map((status, index) => {
                                    const isPast = visibleActiveStageIndex >= index;
                                    const isActive = visibleActiveStage === status.id;

                                    return (
                                        <button
                                            key={status.id}
                                            type="button"
                                            className="group flex min-w-0 flex-1 flex-col items-center gap-2 bg-transparent px-1 text-center"
                                            onClick={() => updateMatchStage(status.id)}
                                        >
                                            <span
                                                className={`relative z-10 inline-flex h-[22px] w-[22px] items-center justify-center rounded-full border-[1.5px] transition-colors ${
                                                    isActive
                                                        ? 'border-blue-500 bg-blue-500 ring-4 ring-blue-100 dark:ring-blue-900/40'
                                                        : isPast
                                                            ? 'border-emerald-500 bg-emerald-500'
                                                            : 'border-slate-300 bg-white dark:bg-gray-800 dark:border-slate-600 group-hover:border-slate-400'
                                                }`}
                                            >
                                                {isPast && !isActive && (
                                                    <Check className="h-3 w-3 text-white" strokeWidth={3} />
                                                )}
                                            </span>
                                            <span
                                                className={`max-w-[92px] text-xs font-medium leading-tight transition-colors ${
                                                    isActive
                                                        ? 'text-blue-700 dark:text-blue-400'
                                                        : isPast
                                                            ? 'text-emerald-700 dark:text-emerald-400'
                                                            : 'text-slate-500 group-hover:text-slate-700 dark:text-slate-500 dark:group-hover:text-slate-300'
                                                }`}
                                            >
                                                {status.name}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-3 border-t border-slate-100 dark:border-slate-700/60 pt-4">
                        <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-rose-500/90 dark:text-rose-400/80 whitespace-nowrap">
                            <X className="h-3 w-3" strokeWidth={2.5} />
                            {t('matching.timeline.closedStatuses')}
                        </span>
                        <div className="flex flex-wrap items-center gap-1.5 flex-1">
                            {timelineClosedStatuses.map(status => {
                                const isCurrentClosedStatus = currentStatus === status;

                                return (
                                    <button
                                        key={status}
                                        type="button"
                                        onClick={() => updateMatchStage(status)}
                                        className={`inline-flex items-center gap-1 rounded-sm border px-2.5 py-1 text-[11px] font-medium transition-colors ${
                                            isCurrentClosedStatus
                                                ? 'border-rose-300 bg-rose-100 text-rose-700 dark:border-rose-800 dark:bg-rose-950/50 dark:text-rose-300'
                                                : 'border-slate-200 bg-white text-slate-500 hover:border-rose-200 hover:bg-rose-50/60 hover:text-rose-600 dark:border-slate-700 dark:bg-gray-800 dark:text-slate-400 dark:hover:border-rose-900/60 dark:hover:bg-rose-950/20 dark:hover:text-rose-300'
                                        }`}
                                    >
                                        {isCurrentClosedStatus && <X className="h-3 w-3" strokeWidth={3} />}
                                        {getStatusLabel(status)}
                                    </button>
                                );
                            })}
                        </div>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <button
                                    type="button"
                                    className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-sm border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 transition-colors hover:border-gray-300 dark:hover:border-gray-600 hover:text-gray-700 dark:hover:text-gray-200"
                                    aria-label={t('matching.timeline.nextStatuses')}
                                >
                                    <HelpCircle className="h-4 w-4" />
                                </button>
                            </TooltipTrigger>
                            <TooltipContent className="max-w-sm space-y-3 p-3">
                                <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{t('matching.timeline.nextStatuses')}</div>
                                <div className="text-sm text-gray-600 dark:text-gray-300">{t('matching.timeline.nextStatusesDescription')}</div>
                                <div className="flex flex-wrap gap-2">
                                    {recommendedStatuses.length > 0 ? recommendedStatuses.map(status => (
                                        <Badge key={status} variant="secondary" className="bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800">
                                            {getStatusLabel(status)}
                                        </Badge>
                                    )) : (
                                        <span className="text-sm text-gray-500 dark:text-gray-400">{t('matching.timeline.noSuggestedStatuses')}</span>
                                    )}
                                </div>
                            </TooltipContent>
                        </Tooltip>
                    </div>
                </div>
            </div>

            {/* Full Report Modal */}
            <Dialog open={reportOpen} onOpenChange={setReportOpen}>
                <DialogContent className="max-w-4xl w-full h-[90vh] max-h-[90vh] overflow-y-auto p-0">
                    <div className="flex flex-col h-full">						<div className="flex justify-between items-center p-4 border-b bg-white dark:bg-gray-800 sticky top-0 z-10">
                        <div className="font-bold text-lg">{t('matching.modal.fullMatchReport')}</div>
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={handleDownloadReport}>{t('matching.modal.savePdf')}</Button>
                            <Button variant="outline" onClick={handleSendByEmail}>{t('matching.modal.sendByEmail')}</Button>
                        </div>
                    </div>
                        <div id="match-report-modal-content" className="overflow-y-auto p-8 bg-white dark:bg-gray-800" style={{ minHeight: '70vh' }}>
                            {/* Digger logo (styled div and text) */}
                            <div style={{ textAlign: 'center', marginBottom: 8 }}>
                                <div style={{
                                    width: 48, height: 48, borderRadius: 12,
                                    background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)",
                                    color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                                    fontWeight: 700, fontSize: 32, margin: "0 auto 8px auto", boxShadow: "0 2px 8px #e0e7ef"
                                }}>
                                    D
                                </div>
                                <span style={{
                                    fontWeight: 700, fontSize: 22, color: "#2563eb", letterSpacing: 1, display: "block", textAlign: "center", marginBottom: 24
                                }}>
                                    Digger
                                </span>
                            </div>							<h1 style={{ fontSize: 28, fontWeight: 700, color: '#0E2356', marginBottom: 8 }}>{t('matching.modal.matchReport')}</h1>
                            <div style={{ marginBottom: 16, color: '#233772', fontSize: 18 }}>
                                {match.candidates?.name} &mdash; {match.jobs?.title} ({match.jobs?.companies?.name})
                            </div>
                            <div style={{ marginBottom: 16 }}>
                                <strong>{t('matching.modal.score')}:</strong> {match.score}%
                            </div>
                            <div style={{ display: 'flex', gap: 32, marginBottom: 24 }}>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ color: '#16a34a', fontWeight: 600, fontSize: 18 }}>{t('matching.sections.keyStrengths')}</h3>
                                    <ul>
                                        {match.strengths?.map((s: string, i: number) => <li key={i}>{s}</li>)}
                                    </ul>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <h3 style={{ color: '#dc2626', fontWeight: 600, fontSize: 18 }}>{t('matching.sections.areasForImprovement')}</h3>
                                    <ul>
                                        {match.weaknesses?.map((w: string, i: number) => <li key={i}>{w}</li>)}
                                    </ul>
                                </div>
                            </div>
                            <div style={{ marginBottom: 24 }}>
                                <h3 style={{ color: '#0E2356', fontWeight: 600, fontSize: 18 }}>{t('matching.sections.aiAnalysis')}</h3>
                                <div style={{ marginBottom: 12 }}>
                                    <strong>{t('matching.modal.overallRecommendation')}:</strong> {match.ai_analysis?.analysis?.final_recommendation}
                                </div>
                                {/* Render all detailed analysis sections */}
                                {match.ai_analysis?.analysis?.matching_analysis && Object.entries(match.ai_analysis.analysis.matching_analysis).map(([section, value]: [string, any]) => (<div key={section} style={{ marginBottom: 16, padding: 12, background: '#f8fafc', borderRadius: 8 }}>
                                    <h4 style={{ color: '#0E2356', fontWeight: 600, fontSize: 16, marginBottom: 4 }}>{section}</h4>
                                    {value.Explanation && <div style={{ marginBottom: 4, whiteSpace: 'pre-line' }}><strong>{t('matching.modal.explanation')}:</strong> {explanationText(value.Explanation)}</div>}
                                    {value["Pair score"] && (
                                        <div style={{ marginBottom: 4 }}>
                                            <strong>{t('matching.analysis.pairScores')}:</strong>
                                            <ul style={{ margin: 0, paddingLeft: 16 }}>
                                                {(Object.entries(value["Pair score"]) as [string, any][]).map(([k, v]) => (
                                                    <li key={k}><span style={{ fontFamily: 'monospace', color: '#64748b' }}>{k.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</span>: {v}</li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}
                                    {value["Match Ratio"] !== undefined && (
                                        <div><strong>{t('matching.analysis.matchRatio')}:</strong> {value["Match Ratio"]}</div>
                                    )}
                                    {value["Weighted Score"] !== undefined && (
                                        <div><strong>{t('matching.analysis.weightedScore')}:</strong> {value["Weighted Score"]}</div>
                                    )}
                                </div>
                                ))}
                            </div>
                            <div style={{ marginTop: 32, fontSize: 12, color: '#64748b', textAlign: 'center' }}>
                                Report generated by Digger
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>			{/* Identity + Match Score */}
            <div className="sticky top-0 z-20 bg-white dark:bg-gray-800 rounded-sm shadow px-6 py-5 w-full mb-4 before:absolute before:left-0 before:right-0 before:-top-4 before:h-4 before:bg-white before:dark:bg-gray-800 before:content-['']">
                <div className="flex flex-col md:flex-row md:items-center gap-6">
                    {/* Candidate → Job identity row */}
                    <div className="flex-1 min-w-0 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-3">
                        {/* Candidate */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="h-11 w-11 shrink-0 bg-digger-light-gray rounded-sm flex items-center justify-center">
                                <User className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold truncate text-gray-900 dark:text-gray-100">{match.candidates?.name}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{match.candidates?.function}</div>
                                <Link to={`/candidates/${match.candidates?.id}`} className="text-xs text-digger-blue hover:underline whitespace-nowrap">{t('matching.view')}</Link>
                            </div>
                        </div>

                        <ArrowRight className="hidden sm:block h-4 w-4 shrink-0 text-gray-300 dark:text-gray-600" />

                        {/* Job */}
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="h-11 w-11 shrink-0 bg-digger-light-gray rounded-sm flex items-center justify-center">
                                <Briefcase className="h-5 w-5 text-gray-600 dark:text-gray-300" />
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="font-semibold truncate text-gray-900 dark:text-gray-100">{match.jobs?.title}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{match.jobs?.companies?.name}</div>
                                <Link to={`/jobs/${match.jobs?.id}`} className="text-xs text-digger-blue hover:underline whitespace-nowrap">{t('matching.view')}</Link>
                            </div>
                        </div>
                    </div>

                    {/* Divider */}
                    <div className="hidden md:block w-px self-stretch bg-slate-100 dark:bg-slate-700/60" />

                    {/* Compact circular score */}
                    <div className="flex items-center gap-3 shrink-0 self-center">
                        <div className="relative">
                            <svg width="84" height="84" className="transform -rotate-90">
                                <circle
                                    cx="42"
                                    cy="42"
                                    r="36"
                                    fill="none"
                                    strokeWidth="7"
                                    className="stroke-gray-200 dark:stroke-gray-700"
                                />
                                <circle
                                    cx="42"
                                    cy="42"
                                    r="36"
                                    fill="none"
                                    stroke="#3B82F6"
                                    strokeWidth="7"
                                    strokeDasharray={`${((match?.ai_analysis?.analysis?.overall_compatibility_score
                                        ? match.ai_analysis.analysis.overall_compatibility_score
                                        : (match.score || 0)) / 100) * 226} 226`}
                                    strokeLinecap="round"
                                    className="transition-all duration-1000 ease-out"
                                />
                            </svg>
                            <div className="absolute inset-0 flex items-center justify-center">
                                <div className="text-lg font-bold text-gray-700 dark:text-gray-200">
                                    {match?.ai_analysis?.analysis?.overall_compatibility_score
                                        ? `${Math.round(match.ai_analysis.analysis.overall_compatibility_score)}%`
                                        : (typeof match.score === 'number' ? `${match.score}%` : '')}
                                </div>
                            </div>
                        </div>
                        <div className="text-xs font-medium text-gray-500 dark:text-gray-400 whitespace-nowrap">{t('matching.matchScore')}</div>
                    </div>
                </div>
            </div>

            {/* Tabs Navigation */}
            <div className="w-full">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <DynamicTabBar
                        tabs={matchTabConfig}
                        pinnedIds={["analysis", "profiles", "comments"]}
                        dynamicSlotCount={3}
                        defaultDynamicIds={["interview-prep", "documents", "tasks"]}
                        activeTabId={activeTab}
                        onTabChange={setActiveTab}
                        entityType="match"
                        userId={user?.id || ""}
                    />

                    {/* Match Analysis Tab Content - Main tab with nested subtabs */}
                    <TabsContent value="analysis" className="md:w-full">
                        <div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6" data-tour="match-score-breakdown">
                            <Tabs defaultValue="overview" className="w-full">
                                <TabsList className="mb-4">
                                    <TabsTrigger value="overview">Overview</TabsTrigger>
                                    <TabsTrigger value="skills-experience">Skills & Experience Match</TabsTrigger>
                                </TabsList>

                                <TabsContent value="overview">
                                    <MatchCircularOverview match={match} />
                                </TabsContent>

                                <TabsContent value="skills-experience">
                                    <SkillsComparisonGraph
                                        matchId={id!}
                                        jobId={match.jobs?.id}
                                        candidateId={match.candidates?.id}
                                        onMissingSkills={setMissingSkills}
                                        onLoadingChange={setMissingSkillsLoading}
                                        onCandidateProfileUpdated={() => {
                                            setIsOutdated(true);
                                            setOutdatedReasons(prev => {
                                                const reason = t('matching.outdated.candidateUpdated');
                                                return prev.includes(reason) ? prev : [...prev, reason];
                                            });
                                        }}
                                    />
                                </TabsContent>
                            </Tabs>
                        </div>
                    </TabsContent>

                    {/* Comments Tab Content */}
                    <TabsContent value="comments" className="md:w-full">
                        <div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
                            <MatchComments matchId={id} />
                        </div>
                    </TabsContent>
                    {/* Interview Prep Tab Content */}
                    <TabsContent value="interview-prep" className="md:w-full">
                        <div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
                            <InterviewPrep matchId={id} />
                        </div>
                    </TabsContent>

                    {/* Documents Tab Content */}
                    <TabsContent value="documents" className="md:w-full">
                        <div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
                            <MatchDocumentsTab matchId={id} />
                        </div>
                    </TabsContent>

                    {/* Tasks Tab Content */}
                    <TabsContent value="tasks" className="md:w-full">
                        <div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
                            <MatchTasksTab 
                                matchId={id || ''} 
                                candidateId={match?.candidates?.id}
                                candidateName={match?.candidates?.name}
                                jobId={match?.jobs?.id}
                                jobTitle={match?.jobs?.title}
                                companyId={match?.jobs?.company?.id}
                                companyName={match?.jobs?.company?.name}
                            />
                        </div>
                    </TabsContent>
                    <TabsContent value="profiles" className="md:w-full">
						<div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
							<MatchProfilesTab profilesData={match?.ai_science_analysis} />
						</div>
                    </TabsContent>
                    {customFields.length > 0 && (
                        <TabsContent value="custom-fields" className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
                            <MatchCustomFieldsTab match={match} refetchMatch={fetchMatch} />
                        </TabsContent>
                    )}
                    <TabsContent value="mailbox" className="bg-card border border-border rounded-sm p-6">
                        <EntityMailbox entityType="match" entityId={id || ''} />
                    </TabsContent>
                    <TabsContent value="history" className="md:w-full">
                        <div className="bg-white dark:bg-gray-800 rounded-sm shadow p-6">
                            <MatchHistoryTab matchId={id || ''} />
                        </div>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Status change confirmation dialog */}
            <Dialog open={statusConfirmDialog.open} onOpenChange={(open) => { if (!open) closeStatusConfirmDialog(); }}>
                <DialogContent className={`${statusConfirmDialog.actions.some(a => a.key === 'create_workforce' && a.checked) ? 'sm:max-w-[780px]' : 'sm:max-w-[560px]'} max-h-[85vh] overflow-hidden`}>
                  <div className="overflow-y-auto max-h-[calc(85vh-2rem)]">
                    <DialogHeader>
                        <DialogTitle>
                            {t(`matching.statusConfirm.title.${statusConfirmDialog.targetStatus}`, t('matching.statusConfirm.titleDefault'))}
                        </DialogTitle>
                        <DialogDescription>
                            {t(`matching.statusConfirm.description.${statusConfirmDialog.targetStatus}`, '')}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        {statusConfirmDialog.actions.map(action => (
                            <div
                                key={action.key}
                                className={`flex items-start gap-3 rounded-sm border p-3 transition-colors ${action.checked ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-800/50 opacity-60'}`}
                            >
                                <Switch
                                    checked={action.checked}
                                    onCheckedChange={() => toggleStatusAction(action.key)}
                                    className="mt-0.5"
                                />
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium">{action.label}</div>
                                    <div className="text-xs text-muted-foreground">{action.description}</div>
                                    {action.value !== undefined && action.checked && (
                                        <Select value={action.value} onValueChange={(v) => updateStatusActionValue(action.key, v)}>
                                            <SelectTrigger className="mt-2 h-8 w-48 text-xs">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="closed/won">{t('jobs.formWizard.statuses.closed/won', 'Closed / Won')}</SelectItem>
                                                <SelectItem value="closed/lost">{t('jobs.formWizard.statuses.closed/lost', 'Closed / Lost')}</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    )}
                                    {action.key === 'create_workforce' && action.checked && (
                                        <div className="mt-3 space-y-2 border-t pt-3">
                                            <div className="grid grid-cols-4 gap-2">
                                                <div className="space-y-1">
                                                    <Label className="text-xs font-medium">
                                                        {t("workforce.fields.startDate", "Start Date")} <span className="text-red-500">*</span>
                                                    </Label>
                                                    <Input
                                                        type="date"
                                                        value={workforceForm.startDate}
                                                        onChange={(e) => setWorkforceForm(f => ({ ...f, startDate: e.target.value }))}
                                                        className="h-7 text-xs"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs font-medium">
                                                        {t("workforce.fields.expectedEndDate", "Expected End Date")}
                                                    </Label>
                                                    <Input
                                                        type="date"
                                                        value={workforceForm.expectedEndDate}
                                                        onChange={(e) => setWorkforceForm(f => ({ ...f, expectedEndDate: e.target.value }))}
                                                        className="h-7 text-xs"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs font-medium">
                                                        {t("workforce.fields.locationType", "Work Type")}
                                                    </Label>
                                                    <Select value={workforceForm.locationType} onValueChange={(v) => setWorkforceForm(f => ({ ...f, locationType: v }))}>
                                                        <SelectTrigger className="h-7 text-xs">
                                                            <SelectValue placeholder={t("common.select", "Select...")} />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {LOCATION_TYPES.map((lt) => (
                                                                <SelectItem key={lt.value} value={lt.value}>
                                                                    {t(`workforce.locationTypes.${lt.value}`, lt.label)}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs font-medium">
                                                        {t("workforce.fields.contractType", "Contract Type")}
                                                    </Label>
                                                    <Select value={workforceForm.contractType} onValueChange={(v) => setWorkforceForm(f => ({ ...f, contractType: v }))}>
                                                        <SelectTrigger className="h-7 text-xs">
                                                            <SelectValue placeholder={t("common.select", "Select...")} />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {CONTRACT_TYPES.map((ct) => (
                                                                <SelectItem key={ct.value} value={ct.value}>
                                                                    {t(`workforce.contractTypes.${ct.value}`, ct.label)}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                            <div className="grid grid-cols-4 gap-2">
                                                <div className="space-y-1">
                                                    <Label className="text-xs font-medium">
                                                        {t("workforce.fields.location", "Location")}
                                                    </Label>
                                                    <Input
                                                        value={workforceForm.location}
                                                        onChange={(e) => setWorkforceForm(f => ({ ...f, location: e.target.value }))}
                                                        placeholder={t("workforce.fields.locationPlaceholder", "Enter location...")}
                                                        className="h-7 text-xs"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs font-medium">
                                                        {t("workforce.fields.purchasePriceDaily", "Purchase (daily)")}
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={workforceForm.purchasePriceDaily}
                                                        onChange={(e) => setWorkforceForm(f => ({ ...f, purchasePriceDaily: e.target.value }))}
                                                        placeholder="0.00"
                                                        className="h-7 text-xs"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs font-medium">
                                                        {t("workforce.fields.sellingPriceDaily", "Selling (daily)")}
                                                    </Label>
                                                    <Input
                                                        type="number"
                                                        min="0"
                                                        step="0.01"
                                                        value={workforceForm.sellingPriceDaily}
                                                        onChange={(e) => setWorkforceForm(f => ({ ...f, sellingPriceDaily: e.target.value }))}
                                                        placeholder="0.00"
                                                        className="h-7 text-xs"
                                                    />
                                                </div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs font-medium">
                                                        {t("workforce.fields.currency", "Currency")}
                                                    </Label>
                                                    <Select value={workforceForm.currency} onValueChange={(v) => setWorkforceForm(f => ({ ...f, currency: v }))}>
                                                        <SelectTrigger className="h-7 text-xs">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {CURRENCY_OPTIONS.map((c) => (
                                                                <SelectItem key={c} value={c}>{c}</SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                            </div>
                                            <div className="space-y-1">
                                                <Label className="text-xs font-medium">
                                                    {t("workforce.statusChange.note", "Note")}
                                                </Label>
                                                <Input
                                                    value={workforceForm.notes}
                                                    onChange={(e) => setWorkforceForm(f => ({ ...f, notes: e.target.value }))}
                                                    placeholder={t("candidates.hiredWorkforce.notesPlaceholder", "Additional notes about this assignment...")}
                                                    className="h-7 text-xs"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={closeStatusConfirmDialog}>
                            {t('common.cancel')}
                        </Button>
                        <Button onClick={handleStatusConfirmApply}>
                            {t('matching.statusConfirm.apply')}
                        </Button>
                    </DialogFooter>
                  </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default MatchDetail;
